import React, { useMemo } from 'react';
import { Segment, Button, Icon } from 'semantic-ui-react';
import groupService from 'src/services/api/groupService';
import { sortObjects, weekdays, scrollUp } from 'src/components/helpers';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
  getGroupSearchActivityNames,
  groupsTranslationMap,
} from 'src/containers/adminDashboard/utils/groupsTranslationMap';

export const getGroupLeaders = (group) => {
  const maleLeader = group?.maleLeader;
  const maleLeaderName = `${maleLeader?.name} ${maleLeader?.lastName}`;

  const femaleLeader = group?.femaleLeader;
  const femaleLeaderName = `${femaleLeader?.name} ${femaleLeader?.lastName}`;

  let leaderName = '';
  if (maleLeader && femaleLeader) {
    leaderName = `${maleLeaderName} y ${femaleLeaderName}`;
  } else if (maleLeader) {
    leaderName = maleLeaderName;
  } else if (femaleLeader) {
    leaderName = femaleLeaderName;
  }
  return leaderName;
};

const getLeaderInitials = (group) => {
  const leader = group?.maleLeader || group?.femaleLeader;
  if (!leader?.name) return '?';
  const first = leader.name.charAt(0) || '';
  const last = leader.lastName?.charAt(0) || '';
  return `${first}${last}`.toUpperCase();
};

const getGroupLocation = (group) => {
  if (group.online && !(group.address || group.neighborhood)) {
    return 'En línea';
  }
  if (group.address || group.neighborhood) {
    return `${group.address || ''}${group.address && group.neighborhood ? ' - ' : ''}${
      group.neighborhood || ''
    }`.trim();
  }
  return 'Por confirmar';
};

const GROUPS_PER_PAGE = 3;

const LeaderSelect = (props) => {
  const { inputs, setInputs, setStep, next, backStep = 'person1', fromConexion = false } = props;
  const [loading, set_loading] = React.useState(true);
  const [groups, set_groups] = React.useState([]);
  const [pageIndex, setPageIndex] = React.useState(0);
  const getCaptchaToken = useRecaptchaToken('create_group_members');
  const getAllGroupsCaptchaToken = useRecaptchaToken('all_couple_groups');

  React.useEffect(() => {
    if (!inputs.activityName) {
      set_loading(false);
      return;
    }

    const fetchGroups = async () => {
      setPageIndex(0);
      set_loading(true);

      const activityNames = getGroupSearchActivityNames(inputs.activityName);
      const captchaRequired = isCaptchaRequired();

      try {
        const settled = await Promise.allSettled(
          activityNames.map(async (activityName) => {
            const captcha = await getAllGroupsCaptchaToken({ skip: !captchaRequired });
            if (captchaRequired && !captcha) return null;

            return groupService.getAllGroups({
              activityName,
              kind: inputs.kind || 'Grupo_Discipulado',
              status: true,
              suspended: false,
              ...(captchaRequired ? { captcha } : {}),
            });
          })
        );

        if (settled.some((result) => result.status === 'fulfilled' && result.value === null)) {
          Swal.fire(RECAPTCHA_SECURITY_ERROR);
          return;
        }

        const merged = [];
        const seen = new Set();
        settled.forEach((result) => {
          const response = result.status === 'fulfilled' ? result.value : null;
          if (!response?.data?.success || !response.data.groups) return;
          response.data.groups.forEach((group) => {
            if (seen.has(group.id)) return;
            seen.add(group.id);
            merged.push(group);
          });
        });
        set_groups(merged);
      } catch (err) {
        set_groups([]);
      } finally {
        set_loading(false);
      }
    };

    fetchGroups();
  }, [inputs.activityName, inputs.kind]);

  const selectGroup = (group) => {
    Swal.fire({
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Confirmar',
      showCancelButton: true,
      title: `¿Está seguro en registrarse en el grupo: ${group.groupName}?`,
      text: `Los ${weekdays[group.dayOfWeek]} ${moment(group.time, 'HH:mm').format('hh:mm a z')}, ${
        group.online && !(group.address || group.neighborhood)
          ? 'virtual'
          : `en ${group.neighborhood}`
      }.`,
    }).then(async (result) => {
      if (result.value) {
        set_loading(true);

        const captchaRequired = isCaptchaRequired();
        const captcha = await getCaptchaToken({ skip: !captchaRequired });
        if (captchaRequired && !captcha) {
          set_loading(false);
          Swal.fire(RECAPTCHA_SECURITY_ERROR);
          return;
        }

        let body = {
          ...inputs,
          group: group.id,
          ...(captchaRequired ? { captcha } : {}),
        };
        groupService
          .saveGroupParticipant(body)
          .then((response) => {
            set_loading(false);
            if (response.data.success) {
              let info = {};
              if (response.data.groupMember && response.data.Attendants) {
                if (response.data.Attendants)
                  info = { ...info, Attendants: response.data.Attendants };
                info = { ...info, groupMember: response.data.groupMember };
                setInputs({ ...info });
                setStep(next);
              }
            } else {
              Swal.fire('Error', response.data.message, 'error');
            }
          })
          .catch((err) => {
            set_loading(false);
          });
      }
    });
  };

  const avaliableGroups = groups?.filter(
    (group) => group.maxCapacity === null || group.GroupMembers.length < group.maxCapacity
  );

  const sortedGroups = useMemo(
    () => sortObjects(avaliableGroups, 'dayOfWeek') || [],
    [avaliableGroups]
  );

  const totalPages = Math.max(1, Math.ceil(sortedGroups.length / GROUPS_PER_PAGE));
  const safePageIndex = pageIndex % totalPages;
  const visibleGroups = sortedGroups.slice(
    safePageIndex * GROUPS_PER_PAGE,
    safePageIndex * GROUPS_PER_PAGE + GROUPS_PER_PAGE
  );
  const showCycleButton = sortedGroups.length > GROUPS_PER_PAGE;

  const searchedActivityNames = getGroupSearchActivityNames(inputs.activityName);
  const showsMultipleCategories = searchedActivityNames.length > 1;
  const categoryLabel = showsMultipleCategories
    ? [
        ...new Set(
          searchedActivityNames.map((name) => groupsTranslationMap[name] || name.replace(/_/g, ' '))
        ),
      ].join(' y ')
    : inputs?.activityName?.replace(/_/g, ' ') || '';
  const isDiscipulado =
    inputs.kind === 'Grupo_Discipulado' || inputs.kindgroup === 'Grupo_Discipulado';
  const hasNoGroups = !loading && avaliableGroups.length === 0;
  const showConexionEmptyMessage = hasNoGroups && fromConexion && isDiscipulado;

  const showNextGroups = () => {
    setPageIndex((prev) => (prev + 1) % totalPages);
    scrollUp();
  };

  const renderEmptyMessage = () => {
    if (showConexionEmptyMessage) {
      return (
        <div className="selectGroup-empty">
          <p>
            En este momento nuestro sistema no logra identificar grupos disponibles de acuerdo a tus
            opciones seleccionadas.
          </p>
          <p>
            <strong>Pero no te preocupes 🙂.</strong> El equipo de discipulado te contactará en los
            próximos días para ayudarte a conectar con un grupo que se ajuste a tus necesidades.
          </p>
          <p>¡Estás en Comunidad, estás en Casa! 🏠</p>
        </div>
      );
    }

    return (
      <p className="selectGroup-empty">
        No se encontraron grupos en esta categoría,{' '}
        {isDiscipulado && 'puedes volver atrás para cambiarla, o '}
        si deseas, escríbenos a nuestro sitio. <br />
        {isDiscipulado ? (
          <a href="https://www.comunidadmde.com/grupos" target="_blank" rel="noreferrer">
            https://www.comunidadmde.com/grupos
          </a>
        ) : (
          <a href="https://www.comunidadmde.com/pasoscrecer" target="_blank" rel="noreferrer">
            https://www.comunidadmde.com/pasoscrecer
          </a>
        )}
      </p>
    );
  };

  const renderGroupCard = (group) => (
    <div key={group.id} className="group-card">
      <div className="group-card-accent" />
      <div className="group-card-body">
        <div className="group-card-top">
          <span className="group-card-badge">
            {showsMultipleCategories
              ? groupsTranslationMap[group.activityName] ||
                (group.activityName || '').replace(/_/g, ' ')
              : categoryLabel}
          </span>
        </div>
        <h3 className="group-card-name">{group.groupName}</h3>
        <div className="group-card-details">
          <div className="group-detail-row">
            <p className="group-detail">
              <Icon name="clock outline" />
              {weekdays[group.dayOfWeek]} a las {moment(group.time, 'HH:mm').format('HH:mm')}
            </p>
            {(group.neighborhood || group.address) && (
              <p className="group-detail group-detail-location">
                <Icon name="map marker alternate" />
                {group.neighborhood || getGroupLocation(group)}
              </p>
            )}
          </div>
          {group.address && <p className="group-detail-address">Dir: {group.address}</p>}
          {group.online && !(group.address || group.neighborhood) && (
            <p className="group-detail">
              <Icon name="map marker alternate" />
              En línea
            </p>
          )}
          {group.observations && (
            <p className="group-detail">
              <Icon name="info circle" />
              {group.observations}
            </p>
          )}
        </div>
        <div className="group-card-footer">
          <div className="group-leader">
            <span className="group-leader-avatar">{getLeaderInitials(group)}</span>
            <p className="group-leader-name">
              <strong>Líder:</strong> {getGroupLeaders(group)}
            </p>
          </div>
          <Button className="group-join-btn" onClick={() => selectGroup(group)}>
            Unirme <Icon name="chevron right" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Segment className="selectGroup">
      {!loading && backStep && (
        <Button className="selectGroup-back" onClick={() => setStep(backStep)}>
          <Icon name="arrow left" /> Atrás
        </Button>
      )}

      <div className="selectGroup-header">
        <span className="selectGroup-badge">Sugerencias personalizadas</span>
        <h2>Grupos Recomendados</h2>
        {!hasNoGroups && (
          <p className="selectGroup-intro">
            Según tu categoría ({categoryLabel}), estos son los grupos con cupo disponible en
            Comunidad MDE.
          </p>
        )}
      </div>

      <div className="selectGroup-list">
        {loading ? (
          <p className="selectGroup-loading">Cargando grupos...</p>
        ) : (
          visibleGroups.map(renderGroupCard)
        )}
        {hasNoGroups && renderEmptyMessage()}
      </div>

      {showCycleButton && !loading && (
        <div className="selectGroup-cycle">
          <button type="button" className="selectGroup-cycle-btn" onClick={showNextGroups}>
            <Icon name="sync alternate" />
            Ver otras propuestas de grupos
          </button>
          <p className="selectGroup-cycle-hint">
            ¿No te acomoda ninguno? Haz clic para ver otros grupos recomendados para ti (Opción{' '}
            {safePageIndex + 1} de {totalPages}).
          </p>
          <p className="selectGroup-cycle-note">
            * Los grupos propuestos rotan en cada registro para asegurar una distribución equitativa
            de nuevos discípulos.
          </p>
        </div>
      )}
    </Segment>
  );
};

export default LeaderSelect;
